import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { orgApi, watiApi } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';

function isSessionOpen(lastInboundAt?: string | null) {
  if (!lastInboundAt) return false;
  return Date.now() - new Date(lastInboundAt).getTime() < 24 * 60 * 60 * 1000;
}

const WhatsAppPage: React.FC = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [mode, setMode] = useState<'TEMPLATE' | 'SESSION'>('TEMPLATE');
  const [templateName, setTemplateName] = useState('');
  const [messageText, setMessageText] = useState('');
  const [phoneDraft, setPhoneDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const selected = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId]
  );
  const sessionOpen = isSessionOpen(conversation?.lastInboundAt);

  const loadEmployees = async () => {
    const params: any = {};
    if (user?.role === 'Managers') params.createdBy = user.id;
    setEmployees(await orgApi.listEmployees(params));
  };

  const loadThread = async (employee: any) => {
    setPhoneDraft(employee.phoneNumber || '');
    const list = await watiApi.listConversations({ employeeId: employee.id });
    const conv = list[0] || null;
    setConversation(conv);
    setMessages(conv?.messages || []);
  };

  useEffect(() => {
    loadEmployees().catch((e) => setError(e.message));
  }, [user?.id]);

  useEffect(() => {
    if (selected) {
      loadThread(selected).catch((e) => setError(e.message));
      const t = setInterval(() => loadThread(selected).catch(() => undefined), 10000);
      return () => clearInterval(t);
    }
  }, [selectedId]);

  const savePhone = async () => {
    if (!selected) return;
    const phone = phoneDraft.replace(/\D/g, '');
    await orgApi.updateEmployee(selected.id, { phoneNumber: phone });
    await loadEmployees();
    setSelectedId(selected.id);
  };

  const send = async () => {
    if (!selected) return;
    const whatsappNumber = (selected.phoneNumber || phoneDraft).replace(/\D/g, '');
    if (!whatsappNumber) {
      setError('Save a WhatsApp number first');
      return;
    }
    try {
      setSending(true);
      setError(null);
      let result: any;
      let body = '';
      if (mode === 'TEMPLATE') {
        result = await watiApi.send({
          action: 'sendTemplate',
          whatsappNumber,
          templateName,
          broadcastName: `mgr-${selected.id}-${Date.now()}`,
          parameters: [],
        });
        body = `[Template: ${templateName}]`;
      } else {
        result = await watiApi.send({
          action: 'sendSession',
          whatsappNumber,
          messageText,
        });
        body = messageText;
      }
      if (!result.success) throw new Error(result.error || 'Send failed');

      let conv = conversation;
      if (!conv) {
        conv = await watiApi.createConversation({
          employeeId: selected.id,
          whatsappNumber,
        });
      }
      await watiApi.createMessage({
        conversationId: conv.id,
        direction: 'OUTBOUND',
        messageType: mode,
        body,
        templateName: mode === 'TEMPLATE' ? templateName : null,
        status: 'SENT',
        watiLocalMessageId: result.localMessageId || null,
      });
      setMessageText('');
      await loadThread(selected);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        WhatsApp Messaging
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ maxHeight: 560, overflow: 'auto' }}>
          <List dense>
            {employees.map((e) => (
              <ListItemButton key={e.id} selected={e.id === selectedId} onClick={() => setSelectedId(e.id)}>
                <ListItemText primary={e.name} secondary={e.phoneNumber || 'No number'} />
              </ListItemButton>
            ))}
          </List>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, minHeight: 480 }}>
          {!selected ? (
            <Typography color="text.secondary">Select an employee</Typography>
          ) : (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6">{selected.name}</Typography>
                  {sessionOpen ? <Chip size="small" color="success" label="24h session open" /> : <Chip size="small" label="Use template" />}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField size="small" fullWidth label="WhatsApp number" value={phoneDraft} onChange={(e) => setPhoneDraft(e.target.value)} />
                <Button variant="outlined" onClick={savePhone}>Save</Button>
              </Stack>
              <Box sx={{ bgcolor: 'grey.50', p: 2, mb: 2, minHeight: 220, maxHeight: 280, overflow: 'auto' }}>
                {messages.length === 0 && <Typography color="text.secondary">No messages yet</Typography>}
                {messages.map((m: any) => (
                  <Box key={m.id} sx={{ textAlign: m.direction === 'OUTBOUND' ? 'right' : 'left', mb: 1 }}>
                    <Paper sx={{ display: 'inline-block', p: 1, maxWidth: '80%' }}>
                      <Typography variant="body2">{m.body}</Typography>
                      <Typography variant="caption">{m.status} · {new Date(m.createdAt).toLocaleString()}</Typography>
                    </Paper>
                  </Box>
                ))}
              </Box>
              <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <Button size="small" variant={mode === 'TEMPLATE' ? 'contained' : 'outlined'} onClick={() => setMode('TEMPLATE')}>Template</Button>
                <Button size="small" variant={mode === 'SESSION' ? 'contained' : 'outlined'} disabled={!sessionOpen} onClick={() => setMode('SESSION')}>Session</Button>
              </Stack>
              {mode === 'TEMPLATE' ? (
                <TextField fullWidth size="small" label="Template name" value={templateName} onChange={(e) => setTemplateName(e.target.value)} sx={{ mb: 1 }} />
              ) : (
                <TextField fullWidth multiline minRows={2} value={messageText} onChange={(e) => setMessageText(e.target.value)} sx={{ mb: 1 }} />
              )}
              <Button variant="contained" color="success" disabled={sending} onClick={send}>
                {sending ? 'Sending…' : 'Send WhatsApp'}
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default WhatsAppPage;
